/**
 * @file Column that calculates realization hours by summing Effort * SumFactor of the spendtimes.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ColumnBase } from '../../base/columns/column-base/column-base';
import { ColumnProvider } from '../../base/column-provider';
import { durationToText } from '../../core/helpers/durationToText';
import * as moment from 'moment';
import { Entity, EntityType, SaveResult } from 'breeze-client';
import { DomainService } from '../../domain.service';
import { DataSet } from '../../core/helpers/data-set';
import { ComponentRegistry } from '../../core/component-registry';
import { from } from 'rxjs';
/**
 * Column that calculates realization hours by summing Effort * SumFactor of the spendtimes.
 * @export
 * @class ColumnRealizationComponent
 * @extends {ColumnBase}
 * @implements {OnDestroy}
 */
var ColumnRealizationComponent = /** @class */ (function (_super) {
    tslib_1.__extends(ColumnRealizationComponent, _super);
    /**
     * Constructs a ColumnRealizationComponent.
     * @param provider Provides the configuration information for the column.
     */
    function ColumnRealizationComponent(provider, changeDetector, domainService) {
        var _this = _super.call(this, provider, changeDetector) || this;
        _this.domainService = domainService;
        /**
         * The entity which is the subject of the realization hours.
         * @memberOf ColumnRealizationComponent
         */
        _this.entity = _this.provider.entity;
        /**
         * The EntityType of the SpendTime entity.
         * Is needed for SpendTime queries
         * @private
         * @type {EntityType}
         * @memberOf ColumnRealizationComponent
         */
        _this.spendTimeType = undefined;
        /**
         * The calculated realization of work objects.
         * @private
         * @memberOf ColumnRealizationComponent
         */
        _this.sumSpendTimes = '';
        _this.entity = _this.provider.entity;
        if (_this.entity.entityType.shortName !== 'SpendTime') {
            _this.spendTimeType = domainService.getEntityType('SpendTime');
            _this.realizationWork();
            _this.subscriptionPool.add(domainService.commitFinishedEvent.subscribe(function (result) {
                var match = result.entities.find(function (it) { return it.entityType.shortName === 'SpendTime' && it['Account_Oid'] === _this.entity['Oid']; });
                if (match) {
                    _this.realizationWork();
                }
            }));
        }
        return _this;
    }
    /**
     * Register this column class for the column provider.
     */
    ColumnRealizationComponent.register = function () {
        ComponentRegistry.register('column', 'realization', ColumnRealizationComponent);
    };
    /**
     * Called just before Angular destroys the component.
     */
    ColumnRealizationComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
    };
    Object.defineProperty(ColumnRealizationComponent.prototype, "durationString", {
        /**
         * Create human-readable presentation of the duraction
         */
        get: function () {
            switch (this.entity.entityType.shortName) {
                case 'SpendTime':
                    return this.realizationSpendTime();
                case 'Attendant':
                case 'Appointment':
                case 'Task':
                case 'Assignment':
                    if (!this.sumSpendTimes) {
                        this.realizationWork();
                    }
                    return this.sumSpendTimes;
                default:
                    throw new Error(this.entity.entityType.shortName + ' has no SpendTimes! See code of ColumnRealizationComponent.durationString method.');
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Tries to start an optimised change detection based upon entiy property changes.
     * The component will fallback on angular change detection when failing.
     * @protected
     */
    ColumnRealizationComponent.prototype.startOptimisedChangeDetection = function () {
        var _this = this;
        if (this.provider.entity) {
            this.changeDetector.detach();
            var entity = this.provider.entity;
            if (entity.entityType.shortName !== 'SpendTime') {
                return;
            }
            this.propertyChangedSubscription = entity.entityAspect.propertyChanged.subscribe(function (data) {
                _this.changeDetector.detectChanges();
            });
        }
    };
    /**
     * Calculates realization hours by summing Effort * SumFactor of SpendTimes belonging to the work entity.
     * @private
     * @memberOf ColumnRealizationComponent
     */
    ColumnRealizationComponent.prototype.realizationWork = function () {
        var _this = this;
        if (!this.spendTimeType) {
            return;
        }
        var dataSet = new DataSet(this.domainService);
        dataSet.entityType = this.spendTimeType;
        dataSet.sourceName = 'SpendTime';
        dataSet.queryOptions = {
            parameters: {
                className: this.entity.entityType.shortName,
                oid: this.entity['Oid']
            }
        };
        // https://stackoverflow.com/questions/47787575/why-does-detectchanges-method-cause-attempt-to-use-a-destroyed-view-error
        var observable = from(dataSet.refresh());
        this.subscriptionPool.add(observable.subscribe(function (result) {
            if (result.results.length === 0) {
                if (_this.sumSpendTimes !== '') {
                    _this.sumSpendTimes = '';
                    _this.changeDetector.detectChanges();
                }
                return;
            }
            var sum = 0;
            result.results.map(function (it) { return it; }).forEach(function (item) {
                sum += moment.duration(item.Effort).asMinutes() * item.SumFactor;
            });
            var value = '';
            if (sum < 0) {
                value = '-' + durationToText(moment.duration(-sum, 'minutes'));
                if (value !== _this.sumSpendTimes) {
                    _this.sumSpendTimes = value;
                    _this.changeDetector.detectChanges();
                }
                return;
            }
            value = durationToText(moment.duration(sum, 'minutes'));
            if (value !== _this.sumSpendTimes) {
                _this.sumSpendTimes = value;
                _this.changeDetector.detectChanges();
            }
        }));
    };
    /**
     * Gives value Effort * SumFactor of the SpendTime as realization hours.
     * @private
     * @returns {string} The calculated value.
     * @memberOf ColumnRealizationComponent
     */
    ColumnRealizationComponent.prototype.realizationSpendTime = function () {
        var factor = this.entity['SumFactor'];
        if (factor === 0) {
            return '0';
        }
        var duration = moment.duration(this.entity['Effort']);
        if (duration) {
            var text = durationToText(duration);
            switch (factor) {
                case -1:
                    return '-' + text;
                default:
                    return text;
            }
        }
        else {
            return '';
        }
    };
    return ColumnRealizationComponent;
}(ColumnBase));
export { ColumnRealizationComponent };
